/**
 * FreeFormPhotoPage.jsx — Bölüm 25-E
 * Free Form submission fotoğraf yükleme / görüntüleme ekranı.
 * Route: /FdsSaha/FreeForm/:templateId/photos/:submissionId
 *
 * Akış:
 *  1. Template tree yükle → PHOTO tipindeki soruları çek
 *  2. Her soru için mevcut fotoğrafları listele (getFreeFormPhotos)
 *  3. Yeni fotoğraf yükle (uploadFreeFormPhoto)
 *  4. Fotoğrafı büyük görüntüle (lightbox)
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams }                           from "react-router-dom";
import {
  Alert, Badge, Button, Card, CardBody,
  Container, Modal, ModalBody, Spinner,
} from "reactstrap";
import {
  Camera, CheckCircle2, ChevronLeft,
  ImageOff, UploadCloud, X,
} from "lucide-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  getFreeFormPhotos,
  getFreeFormPhotoUrl,
  getTemplateTree,
  uploadFreeFormPhoto,
  flatFreeFormNodes,
} from "../../services/freeFormService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// ─── Yardımcı: tekil fotoğraf kartı ─────────────────────────────────────────

function PhotoThumb({ photo, onPreview }) {
  const { t } = useTranslation("saha");
  const url = getFreeFormPhotoUrl(photo.storedFileName || photo.fileName || photo.storedName);
  if (!url) return null;
  return (
    <button
      type="button"
      className="border-0 bg-transparent p-0 position-relative"
      style={{ width: 80, height: 80 }}
      onClick={() => onPreview(url)}
      title={t("freeFormPhoto.buyut")}
    >
      <img
        src={url}
        alt={t("freeFormPhoto.foto")}
        className="rounded object-fit-cover w-100 h-100"
        style={{ objectFit: "cover" }}
      />
    </button>
  );
}

// ─── Yardımcı: soru fotoğraf paneli ─────────────────────────────────────────

function QuestionPhotoPanel({ question, submissionId, onPreview }) {
  const { t } = useTranslation("saha");
  const qId                     = question._id || question.id;
  const inputRef                = useRef(null);
  const [photos,   setPhotos]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [uploading,setUploading]= useState(false);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(false);

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFreeFormPhotos(submissionId, qId);
      // API yanıtı: { result, data: [...] } veya doğrudan dizi
      setPhotos(res?.data || res || []);
    } catch {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [submissionId, qId]);

  useEffect(() => {
    async function run() { await loadPhotos(); }
    run();
  }, [loadPhotos]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    setSuccess(false);
    try {
      await uploadFreeFormPhoto(submissionId, file, qId);
      setSuccess(true);
      await loadPhotos();
    } catch (err) {
      setError(err?.response?.data?.message || t("freeFormPhoto.fotografYuklenemedi"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="p-3 border-bottom">
      {/* Soru başlığı */}
      <div className="d-flex align-items-center gap-2 mb-2">
        <Camera size={14} className="text-primary flex-shrink-0" />
        <span className="small fw-semibold flex-grow-1">{question.name}</span>
        {question.required && (
          <Badge color="danger" pill style={{ fontSize: "0.6rem" }}>{t("freeFormPhoto.zorunlu")}</Badge>
        )}
        <Badge color="soft-secondary" style={{ fontSize: "0.6rem" }}>
          {loading ? "…" : photos.length}{" "}{t("freeFormPhoto.foto_2")}
        </Badge>
      </div>

      {/* Mevcut fotoğraflar */}
      {loading ? (
        <Spinner size="sm" color="primary" className="my-1" />
      ) : photos.length === 0 ? (
        <div className="d-flex align-items-center gap-1 text-muted small mb-2">
          <ImageOff size={12} />
          <span>{t("freeFormPhoto.henuzFotografYok")}</span>
        </div>
      ) : (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {photos.map((p, i) => (
            <PhotoThumb key={p.id || i} photo={p} onPreview={onPreview} />
          ))}
        </div>
      )}

      {/* Bildirimler */}
      {error   && <Alert color="danger"  className="py-1 px-2 small mb-2">{error}</Alert>}
      {success && (
        <Alert color="success" className="py-1 px-2 small mb-2 d-flex align-items-center gap-1">
          <CheckCircle2 size={12} />{" "}{t("freeFormPhoto.fotografYuklendi")}
        </Alert>
      )}

      {/* Yükle butonu */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="d-none"
        onChange={handleFileChange}
      />
      <Button
        color="soft-primary"
        size="sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? (
          <>
            <Spinner size="sm" className="me-1" />
            {t("freeFormPhoto.yukleniyor")}
          </>
        ) : (
          <>
            <UploadCloud size={13} className="me-1" />
            {t("freeFormPhoto.fotografEkle")}
          </>
        )}
      </Button>
    </div>
  );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────

export default function FreeFormPhotoPage() {
  const { t } = useTranslation("saha");
  const { templateId, submissionId } = useParams();
  const navigate                     = useNavigate();

  const [photoQuestions, setPhotoQuestions] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(null);

  // Lightbox
  const [previewUrl, setPreviewUrl] = useState(null);

  // ── Template tree yükle → PHOTO soruları çek ──────────────────────────────
  useEffect(() => {
    document.title = i18n.t("saha:freeFormPhoto.fotografYukleFds");
  }, []);

  useEffect(() => {
    if (!templateId) return;
    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const tree = await getTemplateTree(templateId);
        // Backend "rootNodes" key kullanıyor → flatFreeFormNodes ile düzleştir
        const allNodes = flatFreeFormNodes(tree);
        const photos = allNodes.filter((n) => n.answerType === "PHOTO");
        setPhotoQuestions(photos);
      } catch (err) {
        setLoadError(err?.response?.data?.message || i18n.t("saha:freeFormPhoto.sablonYuklenemedi"));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [templateId]);

  // ─── Render: Yükleniyor ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2">{t("freeFormPhoto.yukleniyor")}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("freeFormPhoto.fotografYukle")} pageTitle={t("freeFormPhoto.fdsSaha")} />
          <Alert color="danger">{loadError}</Alert>
          <Button color="light" onClick={() => navigate(-1)}>
            <ChevronLeft size={14} className="me-1" />{t("common:geri")}
          </Button>
        </Container>
      </div>
    );
  }

  // ─── Render: Ana ─────────────────────────────────────────────────────────

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("freeFormPhoto.fotografYukle")} pageTitle={t("freeFormPhoto.fdsSaha")} />

        {/* Üst bilgi kartı */}
        <Card className="border-0 shadow-sm mb-3">
          <CardBody className="py-3">
            <div className="d-flex align-items-center gap-2">
              <Camera size={18} className="text-primary" />
              <div className="flex-grow-1">
                <h6 className="fw-bold mb-0">{t("freeFormPhoto.fotografYukleme")}</h6>
                <span className="text-muted small">
                  {t("freeFormPhoto.submissionFotografSorusu", { submissionId, length: photoQuestions.length })}
                </span>
              </div>
              <Button color="light" size="sm" onClick={() => navigate(-1)}>
                <ChevronLeft size={14} className="me-1" />{t("common:geri")}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Fotoğraf soruları */}
        {photoQuestions.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center py-5">
              <ImageOff size={40} className="text-muted mb-2" />
              <p className="text-muted small mb-0">
                {t("freeFormPhoto.buSablondaFotografSorusuBulunmuyor")}
              </p>
            </CardBody>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
              {photoQuestions.map((q, i) => (
                <QuestionPhotoPanel
                  key={q._id || q.id || i}
                  question={q}
                  submissionId={submissionId}
                  onPreview={setPreviewUrl}
                />
              ))}
            </CardBody>
          </Card>
        )}

        {/* Bitti butonu */}
        <div className="mt-3 mb-4">
          <Button
            color="success"
            className="w-100"
            onClick={() => navigate(-1)}
          >
            <CheckCircle2 size={14} className="me-1" />
            {t("freeFormPhoto.tamamlandiGeriDon")}
          </Button>
        </div>
      </Container>

      {/* ── Lightbox Modal ─────────────────────────────────────────────────── */}
      <Modal
        isOpen={!!previewUrl}
        toggle={() => setPreviewUrl(null)}
        centered
        size="xl"
        contentClassName="bg-transparent border-0 shadow-none"
      >
        <ModalBody className="p-0 position-relative text-center">
          <button
            type="button"
            className="btn btn-dark btn-sm position-absolute top-0 end-0 m-2 rounded-circle"
            style={{ zIndex: 10, width: 32, height: 32, padding: 0 }}
            onClick={() => setPreviewUrl(null)}
          >
            <X size={14} />
          </button>
          {previewUrl && (
            <img
              src={previewUrl}
              alt={t("freeFormPhoto.buyukGorunum")}
              className="img-fluid rounded"
              style={{ maxHeight: "85vh", objectFit: "contain" }}
            />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
}


